import React, { useEffect, useState, useCallback, useMemo, useRef } from 'react';
import { db, auth } from '../firebase';
import { doc, runTransaction, updateDoc, Timestamp } from 'firebase/firestore';
import { getCloudFunctionUrl } from '../config';
import './SalesHistory.css';
import './AdminDashboard.css';
import Spinner from './Spinner';
import Notification from './Notification';
import ImprimirTicket from './ImprimirTicket';
import RemitoPrintInline from './RemitoPrintInline';
import { printShippingLabel } from '../utils/shippingLabelPrint';
import { getInvoiceStatusMeta, hasCreditNoteCae } from '../utils/invoiceStatus';
import { isConsignmentSale, isUnpaidSale } from '../utils/paymentMethods';
import { isViewerFromStorage } from '../utils/staffAccess';
import OrderCustomerInfo from './OrderCustomerInfo';
import OrderCustomerEmailModal from './OrderCustomerEmailModal';

const ITEMS_PER_PAGE = 10;
const PAGE_SIZE_OPTIONS = [10, 25, 50];
const NOTIFICATION_DURATION = 3000;

function reviveFirestoreTimestampsInSale(sale) {
  if (!sale || sale.date == null || typeof sale.date !== 'object') return sale;
  const d = sale.date;
  if (typeof d.seconds === 'number') {
    return { ...sale, date: new Timestamp(d.seconds, d.nanoseconds || 0) };
  }
  return sale;
}

// Genera números de página a mostrar (con elipsis): [1, '...', 4, 5, 6, '...', 10]
const getPageNumbers = (currentPage, totalPages) => {
  if (totalPages <= 7) return Array.from({ length: totalPages }, (_, i) => i + 1);
  const pages = new Set([1, totalPages]);
  for (let i = Math.max(1, currentPage - 1); i <= Math.min(totalPages, currentPage + 1); i++) pages.add(i);
  const sorted = Array.from(pages).sort((a, b) => a - b);
  const result = [];
  let prev = 0;
  for (const p of sorted) {
    if (p > prev + 1) result.push('...');
    result.push(p);
    prev = p;
  }
  return result;
};

// Utilidad mejorada para formatear números
const formatNumber = (number) => {
  if (typeof number !== 'number' || isNaN(number)) return '0,00';
  return number
    .toFixed(2)
    .replace('.', ',')
    .replace(/\B(?=(\d{3})+(?!\d))/g, '.');
};

const fetchWithTimeout = (url, options = {}, timeoutMs = 120000) => {
  const controller = new AbortController();
  const tid = setTimeout(() => controller.abort(), timeoutMs);
  return fetch(url, { ...options, signal: controller.signal }).finally(() => clearTimeout(tid));
};

const toDateValue = (date) => {
  if (!date) return null;
  return typeof date.toDate === 'function' ? date.toDate() : date;
};

const mapSaleToShippingLabelOrder = (sale) => ({
  orderNumber: sale.orderNumber,
  date: toDateValue(sale.date),
  nombre: sale.customerName || '',
  telefono: sale.phone || '',
  domicilio: sale.domicilio || sale.customerData?.address || sale.address || '',
  localidad: sale.localidad || sale.customerData?.city || '',
  codigoPostal: sale.codigoPostal || sale.customerData?.postalCode || '',
  provincia: sale.provincia || sale.customerData?.province || '',
  pais: sale.pais || sale.customerData?.country || 'Argentina',
  informacionAdicional: sale.informacionAdicional || sale.additionalInfo || sale.customerData?.additionalInfo || '',
  cartItems: sale.cartItems || []
});

// Hook personalizado para notificaciones con auto-dismiss
const useNotification = () => {
  const [notification, setNotification] = useState({ message: '', show: false, type: 'info' });

  const showNotification = useCallback((message, type = 'info') => {
    setNotification({ message, show: true, type });
    setTimeout(() => {
      setNotification(prev => ({ ...prev, show: false }));
    }, NOTIFICATION_DURATION);
  }, []);

  return [notification, showNotification];
};

// Hook para debounce de búsquedas
const useDebounce = (value, delay) => {
  const [debouncedValue, setDebouncedValue] = useState(value);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => clearTimeout(handler);
  }, [value, delay]);

  return debouncedValue;
};

// Hook para detectar dispositivos móviles
const useIsMobile = () => {
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  return isMobile;
};

const tabToCollectionName = (tab) => {
  if (tab === 'order') return 'pedidos';
  if (tab === 'mercadoPago') return 'compras_mercadopago';
  return 'ventas_fisicas';
};

const saleToCustomerItem = (sale) => ({
  ...sale,
  nombre: sale.nombre || sale.customerName || '',
  telefono: sale.telefono || sale.phone || '',
  email: sale.email || '',
  domicilio: sale.domicilio || '',
  localidad: sale.localidad || '',
  codigoPostal: sale.codigoPostal || '',
  provincia: sale.provincia || '',
  pais: sale.pais || '',
  informacionAdicional: sale.informacionAdicional || sale.additionalInfo || '',
  cuit: sale.cuit || ''
});

// Componente de detalles optimizado para móviles
const SaleDetails = ({
  sale,
  activeTab,
  isMobile,
  showMessage,
  onCustomerSaved
}) => (
  <div className="renamed-sale-details">
    <h4>Detalles de la {activeTab === 'order' ? 'Pedido' : 'Venta'}</h4>

    <OrderCustomerInfo
      item={saleToCustomerItem(sale)}
      collectionName={tabToCollectionName(activeTab)}
      showMessage={showMessage}
      onSaved={(fields) => onCustomerSaved(sale.id, activeTab, fields)}
    />
    {activeTab === 'physical' && sale.invoiceType ? (
      <p className="sh-invoice-type">Tipo de factura: {sale.invoiceType}</p>
    ) : null}

    <div className="products-section">
      <h5>Productos {isMobile && `(${sale.cartItems.length})`}:</h5>
      <div className="products-grid">
        {sale.cartItems.map((product, i) => (
          <div key={i} className="renamed-product-item">
            {product.image && (
              <img 
                src={product.image} 
                alt={product.model} 
                className="renamed-product-image"
                loading="lazy"
              />
            )}
            <div className="product-info">
              <p><strong>{product.model}</strong></p>
              <p>Cantidad: {product.quantity}</p>
              <p>Precio: ${formatNumber(product.price)}</p>
              {isMobile && (
                <p>Subtotal: ${formatNumber(product.price * product.quantity)}</p>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>

    <div className="price-breakdown">
      <p>Subtotal: <span>${formatNumber(sale.subtotal)}</span></p>
      {sale.discountAmount > 0 && (
        <>
          <p>Descuento: <span>-${formatNumber(sale.discountAmount)}</span></p>
          <p>Tipo: <span>{sale.discountDetail}</span></p>
        </>
      )}
      <p className="total-price">
        <strong>Total: ${formatNumber(sale.totalPrice)}</strong>
      </p>
      {(() => {
        const invoiceMeta = getInvoiceStatusMeta(sale);
        return (
          <p className={`sale-invoice-status is-${invoiceMeta.tone}`}>
            {invoiceMeta.label}
            {hasCreditNoteCae(sale) && sale.creditNoteData?.numero
              ? ` · NC ${sale.creditNoteData.numero}`
              : sale.invoiceData?.numero
                ? ` · ${sale.invoiceData.numero}`
                : ''}
          </p>
        );
      })()}
    </div>
  </div>
);

const SalesHistory = () => {
  // Estados principales
  const [salesData, setSalesData] = useState({
    order: [],
    mercadoPago: [],
    physical: []
  });
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [notification, showNotification] = useNotification();
  const [selectedSale, setSelectedSale] = useState({ id: null, type: null });
  const [showTicketModal, setShowTicketModal] = useState(false);
  const [ticketSaleInfo, setTicketSaleInfo] = useState(null);
  /** Origen del ticket abierto (misma fila) para guardar NC en la colección correcta. */
  const [ticketModalContext, setTicketModalContext] = useState(null);
  const [ncPuntoVenta, setNcPuntoVenta] = useState(8);
  const [ncEmail, setNcEmail] = useState('');
  const [generatingCreditNote, setGeneratingCreditNote] = useState(false);
  const [ncError, setNcError] = useState('');
  const [showRemitoModal, setShowRemitoModal] = useState(false);
  const [remitoSaleInfo, setRemitoSaleInfo] = useState(null);
  const [selectedSaleIds, setSelectedSaleIds] = useState(() => new Set());
  const [emailModalFor, setEmailModalFor] = useState({ sale: null, type: null });
  const [emailToSend, setEmailToSend] = useState('');
  const [sendingEmailFor, setSendingEmailFor] = useState(null);
  const [customerEmailModal, setCustomerEmailModal] = useState(null);

  // Estados de filtros
  const [searchOrder, setSearchOrder] = useState('');
  const [searchRemito, setSearchRemito] = useState('');
  const [searchName, setSearchName] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(ITEMS_PER_PAGE);
  const [activeTab, setActiveTab] = useState('physical');
  const [searchAllDates, setSearchAllDates] = useState(false);
  /** Por defecto: mismo número de pedido (1, 12, 1163…). Desactivar = “contiene” en el texto del número. */
  const [exactSearch, setExactSearch] = useState(true);
  const [hideCancelledSales, setHideCancelledSales] = useState(true); // Por defecto ocultar ventas canceladas
  const [consignmentOnly, setConsignmentOnly] = useState(false);
  const [paymentFilter, setPaymentFilter] = useState('all');
  /** Respuesta del servidor: listas ya filtradas por texto (no re-filtrar en cliente). */
  const [textSearchActiveFromServer, setTextSearchActiveFromServer] = useState(false);
  /** Totales y flags por colección (servidor). */
  const [loadStats, setLoadStats] = useState(() => ({
    order: null,
    mercadoPago: null,
    physical: null,
  }));

  // Hook para detección móvil
  const isMobile = useIsMobile();

  // Fechas con inicialización mejorada
  const getDateRange = useCallback(() => {
    const now = new Date();
    const firstDay = new Date(now.getFullYear(), now.getMonth(), 1);
    const lastDay = new Date(now.getFullYear(), now.getMonth() + 1, 0);
    return { firstDay, lastDay };
  }, []);

  const [startDate, setStartDate] = useState(() => getDateRange().firstDay);
  const [endDate, setEndDate] = useState(() => getDateRange().lastDay);
  const startDateRef = useRef(startDate);
  const endDateRef = useRef(endDate);
  startDateRef.current = startDate;
  endDateRef.current = endDate;

  // Debounce para búsquedas
  const debouncedSearchOrder = useDebounce(searchOrder, 300);
  const debouncedSearchRemito = useDebounce(searchRemito, 300);
  const debouncedSearchName = useDebounce(searchName, 300);

  const collectionsConfig = useMemo(
    () => ({
      order: { collection: 'pedidos' },
      mercadoPago: { collection: 'compras_mercadopago' },
      physical: { collection: 'ventas_fisicas' },
    }),
    []
  );

  const fetchSales = useCallback(async () => {
    setLoading(true);
    try {
      const user = auth.currentUser;
      if (!user) {
        showNotification('Iniciá sesión para ver el historial.', 'error');
        return;
      }
      const token = await user.getIdToken();
      let url;
      try {
        url = getCloudFunctionUrl('getSalesHistoryData');
      } catch (e) {
        showNotification(
          'Falta configurar la URL de getSalesHistoryData (REACT_APP_CF_GET_SALES_HISTORY_DATA o BASE_URL).',
          'error'
        );
        return;
      }
      const start = new Date(startDateRef.current);
      start.setHours(0, 0, 0, 0);
      const end = new Date(endDateRef.current);
      end.setHours(23, 59, 59, 999);
      const payload = {
        searchAllDates,
        hideCancelledSales,
        consignmentOnly,
        searchOrder: debouncedSearchOrder,
        searchRemito: debouncedSearchRemito,
        searchName: debouncedSearchName,
        exactSearch,
      };
      if (!searchAllDates) {
        payload.startMs = start.getTime();
        payload.endMs = end.getTime();
      }
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
      });
      const json = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(json.message || `Error HTTP ${response.status}`);
      }
      if (!json.success) {
        throw new Error(json.message || 'No se pudo cargar el historial.');
      }
      const reviveList = (arr) =>
        (Array.isArray(arr) ? arr : []).map(reviveFirestoreTimestampsInSale);
      setSalesData({
        order: reviveList(json.order),
        mercadoPago: reviveList(json.mercadoPago),
        physical: reviveList(json.physical),
      });
      setLoadStats(json.stats || { order: null, mercadoPago: null, physical: null });
      setTextSearchActiveFromServer(Boolean(json.meta?.textSearchActive));
      const warnings = json.meta?.warnings;
      if (Array.isArray(warnings) && warnings.length > 0) {
        showNotification(warnings[0], 'warning');
      }
    } catch (error) {
      console.error('Error fetching sales:', error);
      showNotification(error?.message || 'Error al cargar las ventas.', 'error');
    } finally {
      setLoading(false);
    }
  }, [
    showNotification,
    debouncedSearchOrder,
    debouncedSearchRemito,
    debouncedSearchName,
    searchAllDates,
    hideCancelledSales,
    consignmentOnly,
    exactSearch,
  ]);

  // Carga automática al cambiar búsqueda / toggles; las fechas se aplican con «Actualizar».
  useEffect(() => {
    fetchSales();
  }, [fetchSales]);

  // Función mejorada para verificar si una fecha está en rango
  const isDateInRange = useCallback((date, start, end) => {
    if (!date) return false;
    const saleDate = date.toDate();
    const startNormalized = new Date(start);
    startNormalized.setHours(0, 0, 0, 0);
    const endNormalized = new Date(end);
    endNormalized.setHours(23, 59, 59, 999);
    return saleDate >= startNormalized && saleDate <= endNormalized;
  }, []);

  // Helper: obtener próximo número de remito único (R-000001, R-000002, ...)
  const getNextRemitoNumber = useCallback(async () => {
    const counterRef = doc(db, 'counters', 'remitoCounter');
    const newNumber = await runTransaction(db, async (tx) => {
      const snap = await tx.get(counterRef);
      let current = 0;
      if (snap.exists()) {
        current = Number(snap.data().currentRemitoNumber) || 0;
      }
      const next = current + 1;
      tx.set(counterRef, { currentRemitoNumber: next }, { merge: true });
      return next;
    });
    const padded = String(newNumber).padStart(6, '0');
    return `R-${padded}`;
  }, []);

  // Filtrado memoizado mejorado - búsqueda exacta o parcial
  const filteredSales = useMemo(() => {
    if (textSearchActiveFromServer) {
      return {
        order: salesData.order,
        mercadoPago: salesData.mercadoPago,
        physical: salesData.physical,
      };
    }

    const searchTerm = debouncedSearchOrder.trim();
    const remitoTerm = debouncedSearchRemito.trim();
    const nameTerm = debouncedSearchName.trim();
    const filterSales = (sales) =>
      sales.filter((sale) => {
        let orderMatch = !searchTerm;

        if (searchTerm) {
          const onum = sale.orderNumber != null ? String(sale.orderNumber) : '';
          orderMatch = exactSearch
            ? onum === searchTerm
            : onum.includes(searchTerm);
        }

        const saleRemito = (sale.remitoNumber ?? '').toString().trim();
        const norm = (s) => s.replace(/^R-?/i, '').replace(/^0+/, '') || s;
        const remitoMatch =
          !remitoTerm ||
          saleRemito.toLowerCase().includes(remitoTerm.toLowerCase()) ||
          (norm(saleRemito) &&
            norm(remitoTerm) &&
            norm(saleRemito).includes(norm(remitoTerm)));

        const nameMatch =
          !nameTerm ||
          sale.customerName.toLowerCase().includes(nameTerm.toLowerCase());

        const dateMatch =
          searchAllDates ||
          isDateInRange(sale.date, startDate, endDate);

        return orderMatch && remitoMatch && nameMatch && dateMatch;
      });

    return {
      order: filterSales(salesData.order),
      mercadoPago: filterSales(salesData.mercadoPago),
      physical: filterSales(salesData.physical),
    };
  }, [
    salesData,
    textSearchActiveFromServer,
    debouncedSearchOrder,
    debouncedSearchRemito,
    debouncedSearchName,
    startDate,
    endDate,
    searchAllDates,
    exactSearch,
    isDateInRange,
  ]);

  // Aplicar "Ocultar ventas canceladas": lista visible para la pestaña actual
  const visibleSales = useMemo(() => {
    let list = filteredSales[activeTab];
    if (hideCancelledSales) list = list.filter((s) => s.isConfirmed);
    if (consignmentOnly) {
      list = activeTab === 'physical' ? list.filter((s) => isConsignmentSale(s)) : [];
    }
    if (paymentFilter === 'paid') list = list.filter((s) => !isUnpaidSale(s));
    if (paymentFilter === 'unpaid') list = list.filter((s) => isUnpaidSale(s));
    return list;
  }, [filteredSales, activeTab, hideCancelledSales, consignmentOnly, paymentFilter]);

  const totalCount = visibleSales.length;
  const totalPages = useMemo(
    () => Math.max(1, Math.ceil(totalCount / pageSize)),
    [totalCount, pageSize]
  );
  const displayedSales = useMemo(
    () =>
      visibleSales.slice(
        (currentPage - 1) * pageSize,
        currentPage * pageSize
      ),
    [visibleSales, currentPage, pageSize]
  );
  const pageNumbers = useMemo(
    () => getPageNumbers(currentPage, totalPages),
    [currentPage, totalPages]
  );

  const tabBadgeCount = useCallback(
    (key) => {
      if (consignmentOnly && key !== 'physical') return 0;
      const hasText = Boolean(
        debouncedSearchOrder.trim() ||
          debouncedSearchRemito.trim() ||
          debouncedSearchName.trim()
      );
      if (consignmentOnly) {
        let list = filteredSales.physical.filter((s) => isConsignmentSale(s));
        if (hideCancelledSales) list = list.filter((s) => s.isConfirmed);
        if (paymentFilter === 'paid') list = list.filter((s) => !isUnpaidSale(s));
        if (paymentFilter === 'unpaid') list = list.filter((s) => isUnpaidSale(s));
        return list.length;
      }
      if (hasText || paymentFilter !== 'all') {
        let list = filteredSales[key];
        if (paymentFilter === 'paid') list = list.filter((s) => !isUnpaidSale(s));
        if (paymentFilter === 'unpaid') list = list.filter((s) => isUnpaidSale(s));
        return list.length;
      }
      const st = loadStats[key];
      if (st && typeof st.totalInQuery === 'number') return st.totalInQuery;
      return filteredSales[key].length;
    },
    [
      consignmentOnly,
      hideCancelledSales,
      paymentFilter,
      debouncedSearchOrder,
      debouncedSearchRemito,
      debouncedSearchName,
      filteredSales,
      loadStats,
    ]
  );

  const anyStatsCapped = useMemo(
    () => Object.values(loadStats).some((s) => s && s.capped),
    [loadStats]
  );

  // Función mejorada para manejar detalles con scroll en móviles
  const toggleSaleDetails = useCallback((id, type) => {
    setSelectedSale(prevSelected => {
      const newSelected = prevSelected.id === id && prevSelected.type === type
        ? { id: null, type: null }
        : { id, type };
      
      // Scroll suave en móviles después de expandir
      if (newSelected.id && isMobile) {
        setTimeout(() => {
          const element = document.querySelector(`[data-sale-id="${id}"]`);
          if (element) {
            element.scrollIntoView({ 
              behavior: 'smooth', 
              block: 'nearest',
              inline: 'start'
            });
          }
        }, 100);
      }
      
      return newSelected;
    });
  }, [isMobile]);

  const goToPage = useCallback((page) => {
    setCurrentPage(Math.max(1, Math.min(page, totalPages)));
  }, [totalPages]);

  // Limpiar selección al cambiar de pestaña
  useEffect(() => {
    setSelectedSaleIds(new Set());
  }, [activeTab]);

  // Ir a página 1 cuando cambian filtros o pestaña
  useEffect(() => {
    setCurrentPage(1);
  }, [debouncedSearchOrder, debouncedSearchRemito, debouncedSearchName, startDate, endDate, activeTab, searchAllDates, exactSearch, hideCancelledSales, consignmentOnly, paymentFilter]);

  // Ajustar página actual si excede total (ej. menos resultados tras filtrar)
  useEffect(() => {
    if (totalPages >= 1 && currentPage > totalPages) {
      setCurrentPage(totalPages);
    }
  }, [totalPages, currentPage]);

  const executeCancelSale = useCallback(async (saleId, saleType, cartItems) => {
    const collectionName = collectionsConfig[saleType].collection;
    const saleRef = doc(db, collectionName, saleId);
    await runTransaction(db, async (transaction) => {
      const saleDoc = await transaction.get(saleRef);
      if (!saleDoc.exists) throw new Error("La venta no existe!");
      const productRefs = cartItems.map(item => doc(db, 'products', item.id));
      const productDocs = await Promise.all(productRefs.map(ref => transaction.get(ref)));
      productDocs.forEach((productDoc, index) => {
        if (!productDoc.exists) throw new Error(`Producto ${cartItems[index].id} no existe!`);
        const newStock = productDoc.data().stock + cartItems[index].quantity;
        transaction.update(productRefs[index], { stock: newStock });
      });
      transaction.update(saleRef, { isConfirmed: false });
    });
  }, [collectionsConfig]);

  const handleCancelSale = useCallback(async (saleId, saleType, cartItems) => {
    const confirmed = window.confirm('¿Estás seguro que deseas cancelar esta venta?');
    if (!confirmed) return;
    setActionLoading(true);
    try {
      await executeCancelSale(saleId, saleType, cartItems);
      showNotification('Venta cancelada y stock repuesto.', 'success');
      fetchSales();
    } catch (error) {
      console.error("Error canceling sale:", error);
      showNotification('Error al cancelar la venta.', 'error');
    } finally {
      setActionLoading(false);
    }
  }, [executeCancelSale, showNotification, fetchSales]);

  const toggleSaleSelection = useCallback((saleId) => {
    setSelectedSaleIds(prev => {
      const next = new Set(prev);
      if (next.has(saleId)) next.delete(saleId);
      else next.add(saleId);
      return next;
    });
  }, []);

  const cancelableDisplayed = useMemo(
    () => displayedSales.filter(s => s.isConfirmed),
    [displayedSales]
  );
  const allCancelableSelected =
    cancelableDisplayed.length > 0 &&
    cancelableDisplayed.every(s => selectedSaleIds.has(s.id));

  const handleSelectAllCancelable = useCallback(() => {
    if (allCancelableSelected) {
      setSelectedSaleIds(new Set());
    } else {
      setSelectedSaleIds(new Set(cancelableDisplayed.map(s => s.id)));
    }
  }, [allCancelableSelected, cancelableDisplayed]);

  const handleCancelSelectedSales = useCallback(async () => {
    const ids = Array.from(selectedSaleIds);
    if (ids.length === 0) {
      showNotification('Selecciona al menos una venta.', 'info');
      return;
    }
    const confirmed = window.confirm(
      `¿Estás seguro que deseas cancelar ${ids.length} venta(s)? Se repondrá el stock de cada una.`
    );
    if (!confirmed) return;
    setActionLoading(true);
    setSelectedSaleIds(new Set());
    let ok = 0;
    let fail = 0;
    try {
      for (const saleId of ids) {
        const sale = displayedSales.find(s => s.id === saleId && s.isConfirmed);
        if (!sale) continue;
        try {
          await executeCancelSale(sale.id, activeTab, sale.cartItems);
          ok++;
        } catch (err) {
          console.error("Error canceling sale:", err);
          fail++;
        }
      }
      fetchSales();
      if (fail === 0) {
        showNotification(`${ok} venta(s) cancelada(s) y stock repuesto.`, 'success');
      } else {
        showNotification(`${ok} cancelada(s), ${fail} error(es). Recarga la página.`, 'error');
      }
    } catch (error) {
      console.error("Error canceling selected sales:", error);
      showNotification('Error al cancelar las ventas seleccionadas.', 'error');
    } finally {
      setActionLoading(false);
    }
  }, [selectedSaleIds, displayedSales, activeTab, executeCancelSale, showNotification, fetchSales]);

  const openTicketModal = useCallback((sale, type) => {
    // Mapear la venta al formato esperado por ImprimirTicket
    const mappedProducts = (sale.cartItems || []).map((item, idx) => ({
      id: item.id || String(item.model || `p${idx}`),
      model: item.model,
      price: Number(item.price) || 0,
      quantity: Number(item.quantity) || 0
    }));

    const dateValue = sale.date && typeof sale.date.toDate === 'function' ? sale.date.toDate() : sale.date;

    const cd = sale.customerData && type === 'physical'
      ? {
          name: sale.customerData.name || sale.customerName || '',
          email: sale.customerData.email || sale.email || '',
          cuit: sale.customerData.cuit || sale.cuit || '',
          cuil: sale.customerData.cuil || sale.customerData.cuit || sale.cuit || '',
          dni: sale.customerData.dni || '',
          documentType: sale.customerData.documentType || '99',
          phone: sale.customerData.phone || sale.phone || '',
          address: sale.customerData.address || '',
          city: sale.customerData.city || '',
          postalCode: sale.customerData.postalCode || '',
          province: sale.customerData.province || '',
          country: sale.customerData.country || 'Argentina'
        }
      : {
          name: sale.customerName || '',
          email: sale.email || '',
          cuit: sale.cuit || '',
          cuil: sale.cuit || '',
          dni: sale.dni || '',
          documentType: sale.documentType || '99',
          phone: sale.phone || '',
          address: sale.address || '',
          city: '',
          postalCode: '',
          province: '',
          country: 'Argentina'
        };

    const mappedSaleInfo = {
      orderNumber: sale.orderNumber,
      date: dateValue,
      products: mappedProducts,
      customerData: cd,
      subtotal: Number(sale.subtotal) || 0,
      discount: sale.discount != null ? Number(sale.discount) : 0,
      discountType: sale.discountType || 'percentage',
      discountAmount: Number(sale.discountAmount) || 0,
      totalPrice: Number(sale.totalPrice) || 0,
      paymentMethods: sale.paymentMethods || undefined,
      invoiceOptions: sale.invoiceOptions || undefined,
      invoiceData: sale.invoiceData || undefined,
      creditNoteData: sale.creditNoteData || undefined
    };

    setTicketSaleInfo(mappedSaleInfo);
    setTicketModalContext({ sale, type });
    setNcPuntoVenta(8);
    setNcEmail(sale.email || cd.email || '');
    setNcError('');
    setShowTicketModal(true);
  }, []);

  const closeTicketModal = useCallback(() => {
    setShowTicketModal(false);
    setTicketSaleInfo(null);
    setTicketModalContext(null);
    setNcError('');
    setGeneratingCreditNote(false);
  }, []);

  // Imprimir ticket en ventana nueva (igual que rótulos/remitos) para que funcione en todos los dispositivos
  const printTicketInNewWindow = useCallback((ticketWrapperEl) => {
    if (!ticketWrapperEl || !ticketWrapperEl.innerHTML) return;

    // Estilos autocontenidos (sin hojas externas) para que la vista previa no quede en blanco en ningún equipo
    const criticalTicketCss = `
*{box-sizing:border-box}
html,body{margin:0;padding:0;background:#fff;color:#1a1a1a;-webkit-print-color-adjust:exact;print-color-adjust:exact}
.ticket-wrapper{width:100%;max-width:100%;font-family:Arial,sans-serif;background:#fff;visibility:visible}
.ticket-content{background:#fff;padding:0;color:#1a1a1a;font-size:12px;line-height:1.35;display:flex;flex-direction:column;min-height:0;visibility:visible}
.ticket-content-inner{padding:16px;flex:1;display:flex;flex-direction:column;visibility:visible}
.ticket-type-banner{display:flex;align-items:center;justify-content:center;width:100%;font-size:18px;font-weight:700;padding:10px 16px;background:#b8d4e8;color:#1a1a1a;border-bottom:2px solid #000}
.ticket-sep{border:none;border-top:1px solid #000;margin:10px 0}
.ticket-header-three-cols{display:grid;grid-template-columns:1fr auto 1fr;gap:12px;font-size:10px;align-items:center;margin-bottom:0}
.ticket-col-emisor{padding:6px 0;min-width:0}
.ticket-logo-small{max-width:192px;max-height:36px;width:auto;height:auto;display:block;margin-bottom:6px;object-fit:contain}
.ticket-col-emisor p{margin:2px 0;line-height:1.35;color:#333}
.ticket-col-tipo{display:flex;align-items:center;justify-content:center;min-width:0}
.ticket-tipo-box{background:#e0e0e0;color:#000;width:64px;min-height:72px;padding:8px 4px;display:flex;flex-direction:column;align-items:center;justify-content:center;text-align:center;flex-shrink:0;border:1px solid #bbb;border-radius:6px}
.ticket-tipo-letra{display:block;font-size:28px;font-weight:700;line-height:1}
.ticket-tipo-codigo{display:block;font-size:11px;margin-top:4px;font-weight:600}
.ticket-tipo-monotributista{display:block;font-size:9px;margin-top:2px}
.ticket-col-datos{text-align:right;min-width:0}
.ticket-col-datos p{margin:2px 0;line-height:1.35;color:#333}
.ticket-factura-title{font-weight:700;font-size:13px;margin-bottom:4px!important}
.ticket-order{margin-bottom:0}
.order-row{display:grid;grid-template-columns:auto 1fr;gap:12px 24px;margin-bottom:4px;font-size:11px;align-items:baseline}
.order-row strong{grid-column:1}
.order-row span{grid-column:2;text-align:right}
.ticket-customer,.ticket-payment,.ticket-invoice{margin:10px 0}
.customer-row,.payment-row,.invoice-row{display:grid;grid-template-columns:auto 1fr;gap:12px 24px;align-items:center;margin:5px 0;font-size:11px}
.products-header2{display:grid;grid-template-columns:1fr 3fr 1.2fr 1.2fr;gap:8px;padding:6px 8px;font-weight:700;font-size:11px;background:#b8d4e8;border:1px solid #000;margin-bottom:0}
.product-row{display:grid;grid-template-columns:1fr 3fr 1.2fr 1.2fr;gap:8px;padding:6px 8px;font-size:11px;border-bottom:1px solid #000}
.ticket-summary{margin-top:15px}
.summary-row{display:grid;grid-template-columns:auto 1fr;gap:12px 24px;margin-bottom:5px;align-items:baseline}
.summary-row span:first-child{grid-column:1}
.summary-row span:last-child{grid-column:2;text-align:right}
.total-row{padding-top:10px;border-top:2px solid #ddd;font-size:16px}
.total-row strong{grid-column:1 / -1;text-align:right;color:#1a1a1a;font-size:14px}
.ticket-footer-afip{display:flex;align-items:flex-start;justify-content:space-between;gap:16px;text-align:left;background:#fff;padding-left:20px;padding-right:20px}
.ticket-qr img{display:block;border:1px solid #ddd}
.ticket-qr-label{font-size:10px;margin:0;color:#666}
.ticket-thanks{margin-top:6px!important;font-weight:bold}
@media print{
.ticket-actions,.print-btn{display:none!important}
body,body *{visibility:visible}
body{margin:0;padding:0;background:#fff;-webkit-print-color-adjust:exact;print-color-adjust:exact}
body .ticket-wrapper{display:block!important;visibility:visible!important;position:relative!important;left:auto;top:auto;width:100%!important;max-width:210mm!important;margin:0;padding:0;background:#fff!important}
body .ticket-wrapper *{visibility:visible!important}
body .ticket-wrapper .ticket-header-three-cols{display:grid!important}
}
`;
    let html = ticketWrapperEl.innerHTML;

    // Incluir logo como data URL para que cargue en cualquier equipo (evita blob/URLs que fallen en la nueva ventana)
    const logoImg = ticketWrapperEl.querySelector('.ticket-logo-small');
    const logoSrc = logoImg?.getAttribute('src');
    if (logoSrc && !logoSrc.startsWith('data:')) {
      const img = new Image();
      img.crossOrigin = 'anonymous';
      let resolved = false;
      const runPrint = () => {
        if (resolved) return;
        resolved = true;
        openAndPrint();
      };
      img.onload = () => {
        try {
          const c = document.createElement('canvas');
          c.width = img.naturalWidth;
          c.height = img.naturalHeight;
          c.getContext('2d').drawImage(img, 0, 0);
          const dataUrl = c.toDataURL('image/png');
          html = html.split(logoSrc).join(dataUrl);
        } catch (_) {}
        runPrint();
      };
      img.onerror = runPrint;
      img.src = logoSrc;
      setTimeout(runPrint, 1500);
    } else {
      openAndPrint();
    }

    function openAndPrint() {
      const win = window.open('', '_blank');
      if (!win) {
        showNotification('Permití ventanas emergentes para imprimir.', 'error');
        return;
      }
      win.document.write(
        `<!DOCTYPE html><html><head><meta charset="utf-8" /><title>Comprobante</title><style>${criticalTicketCss}</style></head><body>${html}</body></html>`
      );
      win.document.close();

      let printed = false;
      const doPrint = () => {
        if (printed) return;
        printed = true;
        win.focus();
        win.print();
        setTimeout(() => win.close(), 400);
      };

      const waitThenPrint = () => {
        if (win.document.readyState !== 'complete') {
          win.onload = waitThenPrint;
          return;
        }
        const imgs = win.document.querySelectorAll('img');
        const pending = Array.from(imgs).filter((i) => !i.complete);
        if (pending.length === 0) {
          setTimeout(() => { win.focus(); setTimeout(doPrint, 300); }, 100);
          return;
        }
        let waited = 0;
        const maxWait = 5000;
        const check = () => {
          if (Array.from(imgs).every((i) => i.complete) || waited >= maxWait) {
            setTimeout(() => { win.focus(); setTimeout(doPrint, 300); }, 100);
            return;
          }
          waited += 200;
          setTimeout(check, 200);
        };
        setTimeout(check, 200);
      };
      win.onload = waitThenPrint;
      setTimeout(waitThenPrint, 2500);
    }
  }, [showNotification]);

  const handlePrintShippingLabel = useCallback((sale, type) => {
    printShippingLabel(mapSaleToShippingLabelOrder(sale), {
      type,
      onPopupBlocked: () => showNotification('Permití ventanas emergentes para imprimir el rótulo.', 'error')
    });
  }, [showNotification]);

  const openRemitoModal = useCallback(async (sale, type) => {
    // Asegurar número de remito único y persistente
    let remitoNumber = sale.remitoNumber || '';
    try {
      if (!remitoNumber) {
        remitoNumber = await getNextRemitoNumber();
        const collectionName = collectionsConfig[type]?.collection;
        if (collectionName) {
          const saleRef = doc(db, collectionName, sale.id);
          await runTransaction(db, async (tx) => {
            const snap = await tx.get(saleRef);
            if (!snap.exists()) return;
            // Solo setear si aún no tiene remitoNumber en la base
            if (!snap.data().remitoNumber) {
              tx.update(saleRef, { remitoNumber });
            }
          });
        }
      }
    } catch (e) {
      console.error('Error generando número de remito:', e);
    }

    // Mapear la venta al formato esperado por RemitoPrintInline
    const mappedProducts = (sale.cartItems || []).map(item => ({
      name: item.model, // Usar 'name' para consistencia con ImprimirRemito
      model: item.model,
      price: Number(item.price) || 0,
      quantity: Number(item.quantity) || 0
    }));

    const dateValue = sale.date && typeof sale.date.toDate === 'function' ? sale.date.toDate() : sale.date;

    const mappedSaleInfo = {
      orderNumber: sale.orderNumber,
      remitoNumber,
      date: dateValue,
      products: mappedProducts,
      customerData: {
        name: sale.customerName || '',
        cuit: sale.cuit || '',
        phone: sale.phone || '',
        address: sale.address || ''
      },
      subtotal: Number(sale.subtotal) || 0,
      discountAmount: Number(sale.discountAmount) || 0,
      totalPrice: Number(sale.totalPrice) || 0,
      paymentMethods: sale.paymentMethods || undefined,
      invoiceOptions: sale.invoiceOptions || undefined,
      invoiceData: sale.invoiceData || undefined,
      // Campos adicionales para el remito
      transportista: sale.transportista || '',
      fechaSalida: sale.fechaSalida || '',
      observaciones: sale.observaciones || ''
    };

    setRemitoSaleInfo(mappedSaleInfo);
    setShowRemitoModal(true);
  }, []);

  const closeRemitoModal = useCallback(() => {
    setShowRemitoModal(false);
    setRemitoSaleInfo(null);
  }, []);

  // Cerrar modal con tecla Escape
  useEffect(() => {
    const onKeyDown = (e) => {
      if (e.key === 'Escape') {
        if (showTicketModal) {
          closeTicketModal();
        }
        if (showRemitoModal) {
          closeRemitoModal();
        }
        if (emailModalFor.sale) {
          setEmailModalFor({ sale: null, type: null });
          setEmailToSend('');
        }
      }
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [showTicketModal, showRemitoModal, emailModalFor.sale, closeTicketModal, closeRemitoModal]);

  const getCollectionName = useCallback((type) => {
    const map = { order: 'pedidos', mercadoPago: 'compras_mercadopago', physical: 'ventas_fisicas' };
    return map[type] || 'ventas_fisicas';
  }, []);

  const saleCanEmitCreditNote = useCallback((sale) => {
    if (!sale?.isConfirmed) return false;
    if (!sale.invoiceData || Number(sale.invoiceData.tipo_comprobante) !== 11) return false;
    if (sale.creditNoteData?.cae) return false;
    return true;
  }, []);

  const submitCreditNoteFromHistory = useCallback(async () => {
    const ctx = ticketModalContext;
    const info = ticketSaleInfo;
    if (!ctx?.sale || !info) return;

    let url;
    try {
      url = getCloudFunctionUrl('generateAfipCreditNoteReal');
    } catch {
      url = '';
    }
    if (!url) {
      showNotification('URL de Cloud Functions no configurada (generateAfipCreditNoteReal).', 'warning');
      return;
    }
    if (!info.invoiceData || Number(info.invoiceData.tipo_comprobante) !== 11) {
      showNotification('Solo podés emitir NC C sobre una Factura C con CAE.', 'warning');
      return;
    }
    if (info.creditNoteData?.cae) {
      showNotification('Esta venta ya tiene nota de crédito registrada.', 'info');
      return;
    }

    const rowSale = ctx.sale;
    const tabType = ctx.type;
    const pv = Number(ncPuntoVenta) || 8;
    const emailToSend = (ncEmail || info.customerData?.email || '').trim();

    const cust = info.customerData || {};
    const saleInfoPayload = {
      orderNumber: Number(info.orderNumber),
      products: (info.products || []).map(p => ({
        id: p.id,
        model: p.model,
        name: p.model,
        price: Number(p.price),
        quantity: Number(p.quantity)
      })),
      customerData: {
        name: cust.name || 'Consumidor Final',
        email: cust.email || '',
        cuit: cust.cuit || '',
        cuil: cust.cuil || cust.cuit || '',
        dni: cust.dni || '',
        documentType: cust.documentType || '99',
        address: cust.address || '',
        city: cust.city || '',
        postalCode: cust.postalCode || '',
        province: cust.province || '',
        country: cust.country || 'Argentina'
      },
      subtotal: Number(info.subtotal),
      discount: info.discount != null ? Number(info.discount) : 0,
      discountType: info.discountType || 'percentage',
      discountAmount: Number(info.discountAmount || 0),
      totalPrice: Number(info.totalPrice),
      invoiceData: info.invoiceData,
      invoiceOptions: { sendByEmail: Boolean(emailToSend && emailToSend.includes('@')), emailAddress: emailToSend }
    };

    const requestPayload = {
      saleData: saleInfoPayload,
      originalInvoice: info.invoiceData,
      puntoVenta: pv,
      sendByEmail: Boolean(emailToSend && emailToSend.includes('@')),
      emailAddress: emailToSend
    };

    setNcError('');
    setGeneratingCreditNote(true);
    try {
      const response = await fetchWithTimeout(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(requestPayload)
      }, 120000);
      if (!response.ok) {
        const errData = await response.json().catch(() => ({}));
        throw new Error(errData.message || errData.error || `HTTP ${response.status}`);
      }
      const result = await response.json();
      if (!result.success || !result.data?.creditNote) {
        throw new Error(result.message || result.error || 'No se recibió nota de crédito de AFIP');
      }
      const creditNoteData = result.data.creditNote;
      const creditNoteWithId = { ...creditNoteData, id: rowSale.id };
      const collectionName = getCollectionName(tabType);
      await updateDoc(doc(db, collectionName, rowSale.id), {
        creditNoteData: creditNoteWithId,
        invoiceVoided: true
      });

      setTicketSaleInfo(prev =>
        prev ? { ...prev, creditNoteData: creditNoteWithId, invoiceVoided: true } : prev
      );
      setTicketModalContext(prev =>
        prev && prev.sale?.id === rowSale.id
          ? { ...prev, sale: { ...prev.sale, creditNoteData: creditNoteWithId } }
          : prev
      );
      setSalesData(prev => ({
        ...prev,
        [tabType]: (prev[tabType] || []).map(s =>
          s.id === rowSale.id ? { ...s, creditNoteData: creditNoteWithId, invoiceVoided: true } : s
        )
      }));

      showNotification('Nota de crédito AFIP generada.', 'success');
      if (result.data?.emailSent && emailToSend) {
        showNotification(`Nota de crédito enviada por email a ${emailToSend}`, 'success');
      }
    } catch (err) {
      const msg = err.message || 'Error al generar nota de crédito';
      setNcError(msg);
      showNotification('Error NC: ' + msg, 'error');
    } finally {
      setGeneratingCreditNote(false);
    }
  }, [
    ticketModalContext,
    ticketSaleInfo,
    ncPuntoVenta,
    ncEmail,
    getCollectionName,
    showNotification
  ]);

  const handleSendInvoiceByEmail = useCallback(async () => {
    const { sale, type } = emailModalFor;
    if (!sale || !emailToSend.trim()) return;
    const email = emailToSend.trim();
    if (!email.includes('@')) {
      showNotification('Ingresá un email válido.', 'warning');
      return;
    }
    let url;
    try {
      url = getCloudFunctionUrl('sendOrderInvoiceByEmail');
    } catch {
      showNotification('Envío por email no configurado. Configurá la URL en el proyecto.', 'warning');
      return;
    }
    setSendingEmailFor(sale.id);
    try {
      const res = await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          collection: getCollectionName(type),
          docId: sale.id,
          email
        })
      });
      if (res.ok) {
        showNotification(`Comprobante enviado a ${email}`, 'success');
        setEmailModalFor({ sale: null, type: null });
        setEmailToSend('');
      } else {
        const err = await res.json().catch(() => ({}));
        showNotification(err.message || err.error || 'Error al enviar el email', 'error');
      }
    } catch (e) {
      showNotification('Error: ' + (e.message || 'Sin conexión'), 'error');
    } finally {
      setSendingEmailFor(null);
    }
  }, [emailModalFor, emailToSend, getCollectionName, showNotification]);

  const openEmailModal = useCallback((sale, type) => {
    setEmailModalFor({ sale, type });
    setEmailToSend(sale.email || '');
  }, []);

  const handleCustomerInfoSaved = useCallback((saleId, tab, fields) => {
    const address = [
      fields.domicilio,
      fields.localidad,
      fields.codigoPostal,
      fields.provincia,
      fields.pais
    ].filter(Boolean).join(', ');
    setSalesData((prev) => ({
      ...prev,
      [tab]: (prev[tab] || []).map((row) => (
        row.id === saleId
          ? {
              ...row,
              customerName: fields.nombre,
              email: fields.email,
              phone: fields.telefono,
              domicilio: fields.domicilio,
              localidad: fields.localidad,
              codigoPostal: fields.codigoPostal,
              provincia: fields.provincia,
              pais: fields.pais,
              informacionAdicional: fields.informacionAdicional,
              additionalInfo: fields.informacionAdicional,
              cuit: fields.cuit || '',
              address
            }
          : row
      ))
    }));
  }, []);

  const openCustomerEmailModal = useCallback((sale, tab) => {
    setCustomerEmailModal({
      item: saleToCustomerItem(sale),
      source: tabToCollectionName(tab || activeTab)
    });
  }, [activeTab]);


  const tabs = [
    { key: 'order', label: 'Ventas por Pedido' },
    { key: 'mercadoPago', label: 'Ventas con MercadoPago' },
    { key: 'physical', label: 'Ventas Físicas' }
  ];

  if (loading) return <Spinner />;

  return (
    <div className="renamed-sales-report">
      <h2>Informe de Ventas</h2>
      
      <Notification 
        message={notification.message} 
        show={notification.show} 
        type={notification.type}
      />

      {anyStatsCapped && (
        <div className="sales-fetch-cap-notice" role="status">
          Hay más ventas en el período de las que se muestran en esta carga (tope del servidor al listar sin búsqueda),
          o se alcanzó el máximo de documentos escaneados en una búsqueda. Acotá fechas o refiná filtros.
        </div>
      )}

      <div className="renamed-tabs">
        {tabs.map(tab => (
          <button
            key={tab.key}
            className={`tab-button ${activeTab === tab.key ? 'active' : ''}`}
            onClick={() => setActiveTab(tab.key)}
          >
            {isMobile ? tab.label.replace('Ventas ', '') : tab.label}
            <span className="tab-count">({tabBadgeCount(tab.key)})</span>
          </button>
        ))}
      </div>

      <div className="renamed-filters">
        <div className="filter-group">
          <input
            type="text"
            placeholder={
              exactSearch
                ? 'N° de pedido exacto (ej. 1163)'
                : 'N° contiene… (ej. 12 → 120, 312)'
            }
            value={searchOrder}
            onChange={(e) => setSearchOrder(e.target.value)}
            className="renamed-filter-input"
          />
          {searchOrder && (
            <button 
              onClick={() => setSearchOrder('')} 
              className="clear-filter-btn"
              title="Limpiar búsqueda por número"
            >
              ✕
            </button>
          )}
        </div>
        
        <div className="filter-group">
          <input
            type="text"
            placeholder="Buscar por N° de Remito"
            value={searchRemito}
            onChange={(e) => setSearchRemito(e.target.value)}
            className="renamed-filter-input"
          />
          {searchRemito && (
            <button 
              onClick={() => setSearchRemito('')} 
              className="clear-filter-btn"
              title="Limpiar búsqueda por remito"
            >
              ✕
            </button>
          )}
        </div>
        
        <div className="filter-group">
          <input
            type="text"
            placeholder="Buscar por Nombre"
            value={searchName}
            onChange={(e) => setSearchName(e.target.value)}
            className="renamed-filter-input"
          />
          {searchName && (
            <button 
              onClick={() => setSearchName('')} 
              className="clear-filter-btn"
              title="Limpiar búsqueda por nombre"
            >
              ✕
            </button>
          )}
        </div>
        
        <div className="date-filter-block">
          <div className="date-filter-group">
            <input
              type="date"
              value={startDate.toISOString().split('T')[0]}
              onChange={(e) => setStartDate(new Date(e.target.value))}
              className="renamed-filter-input"
              disabled={searchAllDates}
            />
            <input
              type="date"
              value={endDate.toISOString().split('T')[0]}
              onChange={(e) => setEndDate(new Date(e.target.value))}
              className="renamed-filter-input"
              disabled={searchAllDates}
            />
          </div>
          <div className="date-filter-actions">
            <button
              type="button"
              className="sales-history-refresh-btn"
              onClick={() => fetchSales()}
              disabled={loading}
              title={
                searchAllDates
                  ? 'Vuelve a traer todas las ventas según los filtros actuales'
                  : 'Carga de nuevo usando el rango desde / hasta'
              }
            >
              {searchAllDates ? 'Recargar datos' : 'Actualizar fechas'}
            </button>
            {!searchAllDates && (
              <span className="date-filter-hint">
                El rango no se aplica solo al elegir fechas: pulsá el botón para cargar ese período.
              </span>
            )}
          </div>
        </div>
        
        <div className="filter-toggle-group">
          <label className="toggle-switch">
            <input
              type="checkbox"
              checked={searchAllDates}
              onChange={(e) => setSearchAllDates(e.target.checked)}
            />
            <span className="toggle-slider"></span>
            <span className="toggle-label">
              {isMobile ? 'Todas las fechas' : 'Buscar en todas las fechas'}
            </span>
          </label>
          
          <label
            className="toggle-switch"
            title={
              exactSearch
                ? 'Solo el pedido cuyo número coincide con lo que escribís.'
                : 'Incluye pedidos cuyo número contiene el texto (p. ej. 12 coincide con 120 o 312).'
            }
          >
            <input
              type="checkbox"
              checked={exactSearch}
              onChange={(e) => setExactSearch(e.target.checked)}
            />
            <span className="toggle-slider"></span>
            <span className="toggle-label">
              {isMobile
                ? exactSearch
                  ? 'N° exacto'
                  : 'N° parcial'
                : exactSearch
                  ? 'Número de pedido exacto'
                  : 'Coincidencia parcial en el N°'}
            </span>
          </label>
          <label className="toggle-switch">
            <input
              type="checkbox"
              checked={hideCancelledSales}
              onChange={(e) => setHideCancelledSales(e.target.checked)}
            />
            <span className="toggle-slider"></span>
            <span className="toggle-label">
              Ocultar ventas canceladas
            </span>
          </label>
          <label
            className="toggle-switch"
            title="Muestra solo ventas físicas entregadas en consignación"
          >
            <input
              type="checkbox"
              checked={consignmentOnly}
              onChange={(e) => {
                const on = e.target.checked;
                setConsignmentOnly(on);
                if (on) setActiveTab('physical');
              }}
            />
            <span className="toggle-slider"></span>
            <span className="toggle-label">
              {isMobile ? 'Solo consignación' : 'Solo ventas en consignación'}
            </span>
          </label>
          <div className="payment-status-filter" role="group" aria-label="Estado de cobro">
            <span className="payment-status-filter__label">Cobro</span>
            {[
              { id: 'all', label: 'Todas' },
              { id: 'paid', label: 'Pagadas' },
              { id: 'unpaid', label: 'Impagas' }
            ].map((opt) => (
              <button
                key={opt.id}
                type="button"
                className={`payment-status-filter__btn is-${opt.id} ${paymentFilter === opt.id ? 'is-active' : ''}`}
                aria-pressed={paymentFilter === opt.id}
                onClick={() => setPaymentFilter(opt.id)}
              >
                {opt.label}
              </button>
            ))}
          </div>
        </div>
        
        {(searchOrder || searchRemito || searchName) && (
          <button 
            onClick={() => {
              setSearchOrder('');
              setSearchRemito('');
              setSearchName('');
            }} 
            className="clear-all-filters-btn"
          >
            Limpiar búsquedas
          </button>
        )}
      </div>

      <div className="renamed-sales-section">
        <div className="section-header">
          <h3>{tabs.find(tab => tab.key === activeTab)?.label}</h3>
          <span className="results-count">
            {displayedSales.length} de {totalCount} resultados
            {(debouncedSearchOrder || debouncedSearchRemito || debouncedSearchName) && (
              <span className="filter-indicator"> - Búsqueda activa</span>
            )}
            {debouncedSearchOrder &&
              (exactSearch ? (
                <span className="filter-indicator"> - N° exacto</span>
              ) : (
                <span className="filter-indicator"> - N° parcial</span>
              ))}
            {searchAllDates && (
              <span className="filter-indicator"> - Todas las fechas</span>
            )}
            {hideCancelledSales && (
              <span className="filter-indicator"> - Ocultando canceladas</span>
            )}
            {consignmentOnly && (
              <span className="filter-indicator"> - Solo consignación</span>
            )}
            {paymentFilter === 'paid' && (
              <span className="filter-indicator"> - Solo pagadas</span>
            )}
            {paymentFilter === 'unpaid' && (
              <span className="filter-indicator"> - Solo impagas</span>
            )}
          </span>
        </div>

        {cancelableDisplayed.length > 0 && (
          <div className="bulk-cancel-toolbar">
            <label className="bulk-select-all">
              <input
                type="checkbox"
                checked={allCancelableSelected}
                onChange={handleSelectAllCancelable}
                aria-label="Seleccionar todas las ventas cancelables"
              />
              <span>Seleccionar todas</span>
            </label>
            {selectedSaleIds.size > 0 && (
              <button
                type="button"
                className="cancel-selected-button"
                onClick={handleCancelSelectedSales}
                disabled={actionLoading}
              >
                {actionLoading ? 'Cancelando...' : `Cancelar seleccionadas (${selectedSaleIds.size})`}
              </button>
            )}
          </div>
        )}

        {displayedSales.length === 0 ? (
          <div className="no-results">
            <p>No se encontraron ventas con los filtros aplicados.</p>
          </div>
        ) : (
          <ul className="renamed-sales-list orders-container">
            {displayedSales.map((sale) => (
              <li
                key={sale.id}
                className={[
                  'order-card',
                  'renamed-sale-card',
                  !sale.isConfirmed ? 'unpaid cancelled-sale' : (isUnpaidSale(sale) ? 'unpaid' : 'paid')
                ].join(' ')}
                data-sale-id={sale.id}
              >
                <div className="order-card-header">
                  <div className="order-info">
                    <h3>
                      {sale.isConfirmed && (
                        <label className="sale-select-checkbox">
                          <input
                            type="checkbox"
                            checked={selectedSaleIds.has(sale.id)}
                            onChange={() => toggleSaleSelection(sale.id)}
                            aria-label={`Seleccionar venta ${sale.orderNumber}`}
                          />
                        </label>
                      )}
                      {activeTab === 'order' ? 'Pedido' : 'Venta'} #{sale.orderNumber}
                    </h3>
                    <div className="order-meta">
                      <span className="order-date">
                        📅 {sale.date.toDate().toLocaleString('es-AR', { dateStyle: 'short', timeStyle: 'short' })}
                      </span>
                      <span className="order-total">💰 ${formatNumber(sale.totalPrice)}</span>
                      <span className="customer-name">{sale.customerName}</span>
                      {(() => {
                        const invoiceMeta = getInvoiceStatusMeta(sale);
                        if (invoiceMeta.tone === 'none' && !isConsignmentSale(sale)) return null;
                        return (
                          <>
                            {isConsignmentSale(sale) && (
                              <span className="sale-invoice-badge is-consignment">
                                {sale.isPaid === true ? 'Consignación cobrada' : 'Consignación'}
                              </span>
                            )}
                            {invoiceMeta.tone !== 'none' && (
                              <span className={`sale-invoice-badge is-${invoiceMeta.tone}`}>
                                {invoiceMeta.label}
                              </span>
                            )}
                          </>
                        );
                      })()}
                    </div>
                  </div>

                  <div className="order-actions">
                    <div className="order-actions-group">
                      <button
                        type="button"
                        onClick={() => toggleSaleDetails(sale.id, activeTab)}
                        className="btn btn-outline"
                        aria-expanded={selectedSale.id === sale.id && selectedSale.type === activeTab}
                      >
                        {selectedSale.id === sale.id && selectedSale.type === activeTab ? 'Ocultar' : 'Detalles'}
                      </button>
                    </div>
                    <div className="order-actions-group">
                      <button
                        type="button"
                        className="btn btn-secondary"
                        title="Imprimir ticket"
                        onClick={() => openTicketModal(sale, activeTab)}
                      >
                        🖨️ Ticket
                      </button>
                      <button
                        type="button"
                        className="btn btn-secondary"
                        title="Imprimir rótulo para envíos"
                        onClick={() => handlePrintShippingLabel(sale, activeTab)}
                      >
                        🖨️ Rótulo
                      </button>
                      <button
                        type="button"
                        className="btn btn-secondary"
                        title="Imprimir remito de transporte"
                        onClick={() => openRemitoModal(sale, activeTab)}
                      >
                        🚚 Remito
                      </button>
                      {!isViewerFromStorage() && (
                        <button
                          type="button"
                          className={sale.lastAdminCustomerEmailAt ? 'btn btn-outline btn-done' : 'btn btn-secondary'}
                          title="Enviar email al cliente con plantilla Priball"
                          onClick={() => openCustomerEmailModal(sale, activeTab)}
                        >
                          {sale.lastAdminCustomerEmailAt ? '📧 Email ✓' : '📧 Email'}
                        </button>
                      )}
                      <button
                        type="button"
                        className="btn btn-secondary"
                        title="Enviar factura por email"
                        onClick={() => openEmailModal(sale, activeTab)}
                      >
                        📧 Factura
                      </button>
                      {hasCreditNoteCae(sale) && (
                        <button
                          type="button"
                          className="btn btn-outline btn-done"
                          title="Nota de crédito emitida; ver desde el ticket"
                          onClick={() => openTicketModal(sale, activeTab)}
                        >
                          NC ✓
                        </button>
                      )}
                      {!isViewerFromStorage() && saleCanEmitCreditNote(sale) && (
                        <button
                          type="button"
                          className="btn btn-secondary"
                          title="Emitir Nota de Crédito C (AFIP)"
                          onClick={() => openTicketModal(sale, activeTab)}
                        >
                          NC AFIP
                        </button>
                      )}
                    </div>
                    {!isViewerFromStorage() && (
                    <div className="order-actions-group">
                      {sale.isConfirmed ? (
                        <button
                          type="button"
                          className="btn btn-warning"
                          title="Cancelar venta y reponer stock"
                          onClick={() => handleCancelSale(sale.id, activeTab, sale.cartItems)}
                          disabled={actionLoading}
                        >
                          ❌ Cancelar
                        </button>
                      ) : (
                        <button type="button" className="btn btn-outline" disabled>
                          {activeTab === 'order' ? 'Sin confirmar' : 'No computada'}
                        </button>
                      )}
                    </div>
                    )}
                  </div>
                </div>

                {selectedSale.id === sale.id && selectedSale.type === activeTab && (
                  <SaleDetails
                    sale={sale}
                    activeTab={activeTab}
                    isMobile={isMobile}
                    showMessage={showNotification}
                    onCustomerSaved={handleCustomerInfoSaved}
                  />
                )}
              </li>
            ))}
          </ul>
        )}

        {totalCount > 0 && (
          <div className="pagination-wrapper">
            <div className="pagination-info">
              <span className="pagination-range">
                {(currentPage - 1) * pageSize + 1}-{Math.min(currentPage * pageSize, totalCount)} de {totalCount}
              </span>
              <label className="pagination-size">
                <span>Mostrar:</span>
                <select
                  value={pageSize}
                  onChange={(e) => {
                    setPageSize(Number(e.target.value));
                    setCurrentPage(1);
                  }}
                  aria-label="Resultados por página"
                >
                  {PAGE_SIZE_OPTIONS.map((n) => (
                    <option key={n} value={n}>{n}</option>
                  ))}
                </select>
              </label>
            </div>
            <nav className="pagination-nav" aria-label="Paginación">
              <button
                type="button"
                className="pagination-btn pagination-first"
                onClick={() => goToPage(1)}
                disabled={currentPage <= 1}
                aria-label="Primera página"
              >
                ««
              </button>
              <button
                type="button"
                className="pagination-btn pagination-prev"
                onClick={() => goToPage(currentPage - 1)}
                disabled={currentPage <= 1}
                aria-label="Página anterior"
              >
                «
              </button>
              <div className="pagination-pages">
                {pageNumbers.map((p, i) =>
                  p === '...' ? (
                    <span key={`ellipsis-${i}`} className="pagination-ellipsis" aria-hidden="true">…</span>
                  ) : (
                    <button
                      key={p}
                      type="button"
                      className={`pagination-btn pagination-page ${currentPage === p ? 'active' : ''}`}
                      onClick={() => goToPage(p)}
                      aria-label={`Página ${p}`}
                      aria-current={currentPage === p ? 'page' : undefined}
                    >
                      {p}
                    </button>
                  )
                )}
              </div>
              <button
                type="button"
                className="pagination-btn pagination-next"
                onClick={() => goToPage(currentPage + 1)}
                disabled={currentPage >= totalPages}
                aria-label="Página siguiente"
              >
                »
              </button>
              <button
                type="button"
                className="pagination-btn pagination-last"
                onClick={() => goToPage(totalPages)}
                disabled={currentPage >= totalPages}
                aria-label="Última página"
              >
                »»
              </button>
            </nav>
            <div className="pagination-page-label">
              Página {currentPage} de {totalPages}
            </div>
          </div>
        )}
      </div>

      {showTicketModal && ticketSaleInfo && (
        <div 
          className="ticket-modal-overlay" 
          onClick={closeTicketModal}
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100vw',
            height: '100vh',
            background: 'rgba(0,0,0,0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000
          }}
        >
          <div 
            className="ticket-modal-content" 
            onClick={(e) => e.stopPropagation()}
            style={{
              background: '#fff',
              borderRadius: '8px',
              padding: '16px',
              maxWidth: '480px',
              width: '95%',
              maxHeight: '90vh',
              overflowY: 'auto',
              boxShadow: '0 10px 30px rgba(0,0,0,0.2)'
            }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
              <h3 style={{ margin: 0 }}>Ticket de Venta</h3>
              <button onClick={closeTicketModal} style={{ border: 'none', background: 'transparent', fontSize: 18, cursor: 'pointer' }}>✕</button>
            </div>
            <ImprimirTicket saleInfo={ticketSaleInfo} onPrintClick={printTicketInNewWindow} />
            {ticketSaleInfo.invoiceData &&
              Number(ticketSaleInfo.invoiceData.tipo_comprobante) === 11 &&
              ticketSaleInfo.creditNoteData?.cae && (
                <div
                  style={{
                    marginTop: 14,
                    padding: '10px 12px',
                    background: '#14532d',
                    color: '#ecfdf5',
                    borderRadius: 6,
                    fontSize: 13,
                    lineHeight: 1.45,
                    border: '1px solid #0f3d22'
                  }}
                >
                  Ya existe nota de crédito registrada (CAE: {String(ticketSaleInfo.creditNoteData.cae)}).
                </div>
              )}
            {ticketSaleInfo.invoiceData &&
              Number(ticketSaleInfo.invoiceData.tipo_comprobante) === 11 &&
              !ticketSaleInfo.creditNoteData?.cae &&
              ticketModalContext?.sale?.isConfirmed && (
                <div
                  style={{
                    marginTop: 14,
                    paddingTop: 14,
                    borderTop: '1px solid #e0e0e0'
                  }}
                >
                  <h4 style={{ margin: '0 0 6px', fontSize: 15 }}>Nota de crédito AFIP</h4>
                  <p style={{ margin: '0 0 12px', fontSize: 12, color: '#555', lineHeight: 1.4 }}>
                    Emite una Nota de Crédito C asociada a esta factura. El punto de venta debe coincidir con el de la factura original. El email es opcional; si lo completás, se envía el PDF como en la factura.
                  </p>
                  <label style={{ display: 'block', fontSize: 12, fontWeight: 600, marginBottom: 4 }}>Punto de venta</label>
                  <input
                    type="number"
                    min={1}
                    max={9999}
                    value={ncPuntoVenta}
                    onChange={(e) => {
                      const v = e.target.value;
                      const n = parseInt(v, 10);
                      if (v === '' || Number.isNaN(n)) setNcPuntoVenta(8);
                      else setNcPuntoVenta(Math.min(9999, Math.max(1, n)));
                    }}
                    disabled={generatingCreditNote}
                    style={{ width: '100%', padding: '8px 10px', marginBottom: 10, borderRadius: 6, border: '1px solid #ccc', boxSizing: 'border-box' }}
                  />
                  <label style={{ display: 'block', fontSize: 12, fontWeight: 600, marginBottom: 4 }}>Email (opcional)</label>
                  <input
                    type="email"
                    value={ncEmail}
                    onChange={(e) => setNcEmail(e.target.value)}
                    disabled={generatingCreditNote}
                    placeholder="cliente@correo.com"
                    style={{ width: '100%', padding: '8px 10px', marginBottom: 10, borderRadius: 6, border: '1px solid #ccc', boxSizing: 'border-box' }}
                  />
                  {ncError ? (
                    <p style={{ color: '#c62828', fontSize: 12, margin: '0 0 10px' }}>{ncError}</p>
                  ) : null}
                  <button
                    type="button"
                    onClick={submitCreditNoteFromHistory}
                    disabled={generatingCreditNote}
                    style={{
                      width: '100%',
                      padding: '10px 14px',
                      borderRadius: 6,
                      border: 'none',
                      background: generatingCreditNote ? '#90a4ae' : '#1565c0',
                      color: '#fff',
                      fontWeight: 600,
                      cursor: generatingCreditNote ? 'not-allowed' : 'pointer',
                      fontSize: 14
                    }}
                  >
                    {generatingCreditNote ? 'Generando nota de crédito…' : 'Emitir nota de crédito'}
                  </button>
                </div>
              )}
          </div>
        </div>
      )}

      {showRemitoModal && remitoSaleInfo && (
        <div 
          className="remito-modal-overlay" 
          onClick={closeRemitoModal}
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100vw',
            height: '100vh',
            background: 'rgba(0,0,0,0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000
          }}
        >
          <div 
            className="remito-modal-content" 
            onClick={(e) => e.stopPropagation()}
            style={{
              background: '#fff',
              borderRadius: '8px',
              padding: '16px',
              maxWidth: '800px',
              width: '95%',
              maxHeight: '90vh',
              overflowY: 'auto',
              boxShadow: '0 10px 30px rgba(0,0,0,0.2)'
            }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
              <h3 style={{ margin: 0 }}>Remito de Transporte</h3>
              <button onClick={closeRemitoModal} style={{ border: 'none', background: 'transparent', fontSize: 18, cursor: 'pointer' }}>✕</button>
            </div>
            <RemitoPrintInline saleInfo={remitoSaleInfo} />
          </div>
        </div>
      )}

      {emailModalFor.sale && (
        <div 
          className="email-invoice-modal-overlay" 
          onClick={() => { setEmailModalFor({ sale: null, type: null }); setEmailToSend(''); }}
        >
          <div className="email-invoice-modal" onClick={(e) => e.stopPropagation()}>
            <div className="email-invoice-modal-header">
              <h3>📧 Enviar factura por email</h3>
              <button
                type="button"
                className="email-invoice-modal-close"
                onClick={() => { setEmailModalFor({ sale: null, type: null }); setEmailToSend(''); }}
                aria-label="Cerrar"
              >
                ✕
              </button>
            </div>
            <p className="email-invoice-modal-desc">
              Venta N° <strong>{emailModalFor.sale.orderNumber}</strong> — {emailModalFor.sale.customerName}
            </p>
            <label className="email-invoice-modal-label">
              Email del destinatario
            </label>
            <input
              type="email"
              className="email-invoice-modal-input"
              placeholder="ejemplo@correo.com"
              value={emailToSend}
              onChange={(e) => setEmailToSend(e.target.value)}
              autoFocus
            />
            <div className="email-invoice-modal-actions">
              <button
                type="button"
                className="email-invoice-modal-btn secondary"
                onClick={() => { setEmailModalFor({ sale: null, type: null }); setEmailToSend(''); }}
              >
                Cancelar
              </button>
              <button
                type="button"
                className="email-invoice-modal-btn primary"
                onClick={handleSendInvoiceByEmail}
                disabled={!emailToSend.trim() || !emailToSend.includes('@') || sendingEmailFor === emailModalFor.sale?.id}
              >
                {sendingEmailFor === emailModalFor.sale?.id ? 'Enviando...' : 'Enviar'}
              </button>
            </div>
          </div>
        </div>
      )}

      {customerEmailModal && (
        <OrderCustomerEmailModal
          item={customerEmailModal.item}
          source={customerEmailModal.source}
          onClose={() => setCustomerEmailModal(null)}
          showMessage={(msg) => showNotification(msg)}
          onSent={() => {
            const sentItem = customerEmailModal.item;
            setSalesData((prev) => {
              const next = { ...prev };
              ['order', 'mercadoPago', 'physical'].forEach((key) => {
                next[key] = (prev[key] || []).map((row) => (
                  row.id === sentItem.id
                    ? { ...row, lastAdminCustomerEmailAt: { seconds: Math.floor(Date.now() / 1000) } }
                    : row
                ));
              });
              return next;
            });
          }}
        />
      )}
    </div>
  );
};

export default SalesHistory;